import type { SnapshotFrom } from 'xstate';
import { assign, setup } from 'xstate';

/**
 * What an agent can be doing (agent state machine §1), mirrored from the server's own enumeration
 * in `packages/contexts/workspace-presence/src/domain/activity.ts`.
 *
 * `busy` and the transitions out of it are derived from capacity server-side, which is why this
 * chart never decides an activity: it is told one.
 */
export const agentActivities = ['offline', 'available', 'notReady', 'busy', 'wrapUp'] as const;
export type AgentActivity = (typeof agentActivities)[number];

/** The activities a person may ask for. `busy` is derived from capacity and is not one of them. */
export const settableActivities = ['offline', 'available', 'notReady', 'wrapUp'] as const;
export type SettableActivity = (typeof settableActivities)[number];

export type Channel = 'voice' | 'chat' | 'email';

export interface ChannelCapacity {
  readonly channel: Channel;
  readonly capacity: number;
  readonly active: number;
  readonly reserved: number;
}

/**
 * An interaction this agent has been offered, as the workspace knows it from the socket.
 *
 * It is this thin because a notification has no payload: the client is told that interaction X was
 * offered and where in its stream that was, and everything else — who is calling, which queue, how
 * long is left — is read back from `/v1` by the screen that shows it (M4-07). What belongs here is
 * only what makes the offer *exist*, which is the part reconciliation has to get right.
 */
export interface OfferedInteraction {
  readonly interactionId: string;
  readonly offeredAt: string;
  readonly sequence: number;
}

/** The agent as `GET /v1/agents/me` describes them. */
export interface AgentSnapshot {
  readonly agentId: string;
  readonly activity: AgentActivity;
  readonly reason?: string | undefined;
  /**
   * The tenant's wrap-up allowance for this agent, where they have one.
   *
   * Part of the session rather than of a screen: it is the agent's own record, the wrap-up timer is
   * the only thing that reads it, and asking `/v1/agents/me` a second time for one number would be
   * a request for something already in hand.
   */
  readonly wrapUpSeconds?: number | undefined;
  readonly capacities: readonly ChannelCapacity[];
  readonly routableChannels: readonly Channel[];
  readonly unreachable: boolean;
  readonly forcedLogout: boolean;
}

export type AgentSessionEvent =
  /** The server's answer to "what am I": applied whole, because it is the truth. */
  | { readonly type: 'snapshot'; readonly snapshot: AgentSnapshot }
  /**
   * The workspace has just caught up after being disconnected and could not be replayed, so what
   * it was holding is no longer vouched for by anything. Offers go; the snapshot that arrives with
   * it says what is true now.
   */
  | { readonly type: 'reconcile'; readonly snapshot: AgentSnapshot }
  | {
      readonly type: 'offered';
      readonly interactionId: string;
      readonly offeredAt: string;
      readonly sequence: number;
    }
  /** The offer is over, however it ended: taken, declined, expired, or the call went elsewhere. */
  | { readonly type: 'offerWithdrawn'; readonly interactionId: string }
  | { readonly type: 'deviceReady'; readonly ready: boolean };

interface AgentSessionContext {
  agentId: string | undefined;
  reason: string | undefined;
  wrapUpSeconds: number | undefined;
  capacities: readonly ChannelCapacity[];
  routableChannels: readonly Channel[];
  offers: readonly OfferedInteraction[];
  unreachable: boolean;
  forcedLogout: boolean;
  deviceReady: boolean;
}

/**
 * One branch per activity, in one place.
 *
 * A statechart cannot compute a target, so "go to whichever activity the snapshot names" is five
 * guarded branches whichever way it is written. Generating them keeps the two events that carry a
 * snapshot from drifting apart, which is the mistake worth preventing — `reconcile` differs from
 * `snapshot` in exactly one action, and that difference should be visible in one line.
 */
function activityBranches(actions: readonly ('applySnapshot' | 'forgetOffers')[]) {
  return agentActivities.map((activity) => ({
    guard: { type: 'becomes' as const, params: { activity } },
    target: `.${activity}`,
    actions: [...actions],
  }));
}

/**
 * The agent's own session: what they are doing, what they can take, and what they have been offered.
 *
 * Every transition here comes from a server fact. There is no optimistic activity change and no
 * locally invented offer, because the only thing worse than a slow workspace is one that shows an
 * agent as available when routing has stopped offering to them (state management §2). An activity
 * command that fails leaves this chart exactly where it was and the error is shown instead.
 *
 * Activity is a *state* and capacity and offers are *context*, which is the honest division: the
 * five activities are the server's state machine and the chart should refuse to be in a sixth,
 * while a capacity row and an offer list are data whose shape does not constrain what may happen
 * next.
 */
export const agentSessionMachine = setup({
  types: {} as { context: AgentSessionContext; events: AgentSessionEvent },
  actions: {
    applySnapshot: assign(({ event }) => {
      if (event.type !== 'snapshot' && event.type !== 'reconcile') {
        return {};
      }
      return {
        agentId: event.snapshot.agentId,
        reason: event.snapshot.reason,
        wrapUpSeconds: event.snapshot.wrapUpSeconds,
        capacities: event.snapshot.capacities,
        routableChannels: event.snapshot.routableChannels,
        unreachable: event.snapshot.unreachable,
        forcedLogout: event.snapshot.forcedLogout,
      };
    }),
    /**
     * Drops every offer the workspace was holding.
     *
     * Only on `reconcile`, never on an ordinary snapshot: a snapshot arrives whenever anything
     * about the agent changes — including being offered a call, which makes them busy — and
     * clearing the list then would make an offer disappear the instant it arrived. After a gap
     * nothing confirms the offers, and the design's rule is that offers which expired while
     * disconnected are gone (real-time updates §4).
     */
    forgetOffers: assign({ offers: [] }),
    addOffer: assign({
      offers: ({ context, event }) => {
        if (event.type !== 'offered') {
          return context.offers;
        }
        // The same offer twice is a duplicate delivery, not a second call.
        const others = context.offers.filter(
          (offer) => offer.interactionId !== event.interactionId,
        );
        return [
          ...others,
          {
            interactionId: event.interactionId,
            offeredAt: event.offeredAt,
            sequence: event.sequence,
          },
        ];
      },
    }),
    removeOffer: assign({
      offers: ({ context, event }) =>
        event.type === 'offerWithdrawn'
          ? context.offers.filter((offer) => offer.interactionId !== event.interactionId)
          : context.offers,
    }),
    rememberDevice: assign({
      deviceReady: ({ context, event }) =>
        event.type === 'deviceReady' ? event.ready : context.deviceReady,
    }),
  },
  guards: {
    /** Whether the snapshot that just arrived says the agent is in this activity. */
    becomes: ({ event }, params: { activity: AgentActivity }) =>
      (event.type === 'snapshot' || event.type === 'reconcile') &&
      event.snapshot.activity === params.activity,
  },
}).createMachine({
  id: 'agentSession',
  context: {
    agentId: undefined,
    reason: undefined,
    wrapUpSeconds: undefined,
    capacities: [],
    routableChannels: [],
    offers: [],
    unreachable: false,
    forcedLogout: false,
    deviceReady: false,
  },
  initial: 'offline',
  // The same four facts mean the same thing in every activity, so they are handled once. The
  // snapshot's own activity is what moves the chart, and it may move it from anywhere: a session
  // that was lost and came back is `offline` until the server says otherwise.
  on: {
    snapshot: activityBranches(['applySnapshot']),
    reconcile: activityBranches(['forgetOffers', 'applySnapshot']),
    offered: { actions: 'addOffer' },
    offerWithdrawn: { actions: 'removeOffer' },
    deviceReady: { actions: 'rememberDevice' },
  },
  states: {
    offline: {},
    available: {},
    notReady: {},
    busy: {},
    wrapUp: {},
  },
});

export type AgentSessionSnapshot = SnapshotFrom<typeof agentSessionMachine>;

/** The activity the chart is in, which is the state's own name. */
export function activityOf(snapshot: AgentSessionSnapshot): AgentActivity {
  return snapshot.value as AgentActivity;
}
